import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';

export default function useReceivablePageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [allocationFilter, setAllocationFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [statusCounts, setStatusCounts] = useState<Record<string, any>>({});
    const [allocationCounts, setAllocationCounts] = useState<Record<string, any>>({});
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const pageSize = 100;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getReceivables({
                page, pageSize,
                search: search || undefined,
                status: statusFilter || undefined,
                allocationStatus: allocationFilter || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setStatusCounts(result.statusCounts || {});
            setAllocationCounts(result.allocationCounts || {});
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, search, statusFilter, allocationFilter, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handleRowClick = (row: any) => setSelectedItem(row);

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, statusCounts, allocationCounts,
        statusFilter, setStatusFilter,
        allocationFilter, setAllocationFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        selectedItem, setSelectedItem,
        refresh, handleRowClick,
    };
}
